import { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Check, Upload, Trash2, Loader2, ImageIcon } from "lucide-react";

function getThumbUrl(img) { return typeof img === 'string' ? img : (img?.thumbnail_url || img?.url || ''); }

export function PhotoPickerDialog({
  open, onClose, images, currentImageIndex,
  onSelectPhoto, onUploadNewPhoto, onRemoveShot, uploading
}) {
  const fileRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    await onUploadNewPhoto(file);
    // Reset input so the same file can be re-selected
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="photo-picker-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Sora, sans-serif' }} className="text-[#020B23]">
            Change Photo
          </DialogTitle>
        </DialogHeader>

        {images.length === 0 ? (
          <div className="text-center py-8">
            <ImageIcon size={24} className="mx-auto text-[#94969C] mb-2" />
            <p className="text-sm text-[#676970]">No photos available. Upload one below.</p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto p-1">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => onSelectPhoto(idx)}
                data-testid={`photo-pick-${idx}`}
                className={`relative rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 ${
                  idx === currentImageIndex
                    ? "border-[#4D11F6] ring-2 ring-[#4D11F6]/30"
                    : "border-[#E2E4E7] hover:border-[#4D11F6]/40"
                }`}
              >
                <div className="aspect-video w-full">
                  <img src={getThumbUrl(img)} alt={`Photo ${idx}`} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <span className="absolute top-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded-full font-medium">
                  #{idx}
                </span>
                {idx === currentImageIndex && (
                  <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#4D11F6] flex items-center justify-center">
                    <Check size={12} color="white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Upload new photo */}
        <div className="border-t border-[#E2E4E7] pt-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="text-xs border-[#E2E4E7]"
          >
            {uploading ? (
              <><Loader2 size={12} className="mr-1.5 animate-spin" /> Uploading...</>
            ) : (
              <><Upload size={12} className="mr-1.5" /> Upload New Photo</>
            )}
          </Button>
        </div>

        {/* Remove shot */}
        <div className="border-t border-[#E2E4E7] pt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemoveShot}
            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 size={12} className="mr-1.5" /> Remove This Shot
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
